/**
 * Pure TypeScript vial measurement and transform utilities.
 * Measures the bounding box of non-transparent pixels in vial images
 * and computes transform parameters so that all product vials appear
 * at an identical visible height and aligned vertical/horizontal position.
 */

export interface VialBoundingBox {
  cx: number; // 0..1 fraction: center X
  cy: number; // 0..1 fraction: center Y
  w: number;  // 0..1 fraction: visible width
  h: number;  // 0..1 fraction: visible height
}

export interface VialTransform {
  scale: number;
  tx: number; // percent shift in X
  ty: number; // percent shift in Y
  originX: number; // percent transform-origin X
  originY: number; // percent transform-origin Y
  limited: boolean;
}

export const REFERENCE_URL =
  "https://tektideslabs.com/wp-content/uploads/2026/09/Tirzepatide-Lab-Vial-Render.png";

// Promise cache for measurement by canonical URL
const measurePromiseCache = new Map<string, Promise<VialBoundingBox | null>>();

// Logged URLs tracker to avoid duplicate console spam
const loggedUrls = new Set<string>();

/**
 * Strips WordPress resized suffix (e.g. -300x300, -768x512) before file extension
 * and returns the full-size canonical URL.
 */
export function getCanonicalVialUrl(url: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url, "http://localhost");
    const pathname = parsed.pathname;
    const cleanPath = pathname.replace(/-\d+x\d+(\.[a-zA-Z0-9]+)$/i, "$1");
    if (url.startsWith("http://") || url.startsWith("https://")) {
      parsed.pathname = cleanPath;
      return parsed.toString();
    }
    return cleanPath;
  } catch {
    return url.replace(/-\d+x\d+(\.[a-zA-Z0-9]+)$/i, "$1");
  }
}

/**
 * Extracts a short display filename for logging purposes.
 */
function getFilenameFromUrl(url: string): string {
  try {
    const parsed = new URL(url, "http://localhost");
    const parts = parsed.pathname.split("/");
    return decodeURIComponent(parts.pop() || url);
  } catch {
    const parts = url.split("/");
    return parts.pop() || url;
  }
}

/**
 * Measures the non-transparent vial pixels in an image at runtime.
 * Returns bounding box as fractions (0..1) of width/height.
 * If CORS or rendering fails or the image is opaque/full (>=98%), resolves null.
 */
export function measureVial(url: string): Promise<VialBoundingBox | null> {
  if (!url || typeof window === "undefined") {
    return Promise.resolve(null);
  }

  const canonical = getCanonicalVialUrl(url);
  const cached = measurePromiseCache.get(canonical);
  if (cached) {
    return cached;
  }

  const promise = new Promise<VialBoundingBox | null>((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.decoding = "async";

      img.onload = () => {
        try {
          const natW = img.naturalWidth || img.width;
          const natH = img.naturalHeight || img.height;
          if (!natW || !natH) {
            resolve(null);
            return;
          }

          // Downscale to ~400x400 max for fast pixel inspection
          const maxDim = 400;
          let drawW = natW;
          let drawH = natH;
          if (drawW > maxDim || drawH > maxDim) {
            if (drawW >= drawH) {
              drawH = Math.max(1, Math.round((drawH * maxDim) / drawW));
              drawW = maxDim;
            } else {
              drawW = Math.max(1, Math.round((drawW * maxDim) / drawH));
              drawH = maxDim;
            }
          }

          const canvas = document.createElement("canvas");
          canvas.width = drawW;
          canvas.height = drawH;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          if (!ctx) {
            resolve(null);
            return;
          }

          ctx.clearRect(0, 0, drawW, drawH);
          ctx.drawImage(img, 0, 0, drawW, drawH);

          let imgData: ImageData;
          try {
            imgData = ctx.getImageData(0, 0, drawW, drawH);
          } catch {
            // Tainted canvas / CORS blocked
            resolve(null);
            return;
          }

          const data = imgData.data;
          const alphaThreshold = 48; // Ignore faint shadow/glow

          let minX = drawW;
          let minY = drawH;
          let maxX = -1;
          let maxY = -1;

          for (let y = 0; y < drawH; y++) {
            const rowOffset = y * drawW * 4;
            for (let x = 0; x < drawW; x++) {
              const a = data[rowOffset + x * 4 + 3];
              if (a > alphaThreshold) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }

          // If no pixels met threshold
          if (maxX < minX || maxY < minY) {
            resolve(null);
            return;
          }

          const boxW = (maxX - minX + 1) / drawW;
          const boxH = (maxY - minY + 1) / drawH;
          const cx = (minX + (maxX - minX + 1) / 2) / drawW;
          const cy = (minY + (maxY - minY + 1) / 2) / drawH;

          // If effectively opaque / covers >= 98% in both dimensions, return null
          if (boxW >= 0.98 && boxH >= 0.98) {
            resolve(null);
            return;
          }

          const box: VialBoundingBox = {
            cx,
            cy,
            w: boxW,
            h: boxH,
          };

          resolve(box);
        } catch {
          resolve(null);
        }
      };

      img.onerror = () => {
        resolve(null);
      };

      // Load using full-size canonical URL when possible
      img.src = canonical;
    } catch {
      resolve(null);
    }
  });

  measurePromiseCache.set(canonical, promise);
  return promise;
}

/**
 * Returns the cached promise measuring the reference vial (Tirzepatide).
 */
export function getReferenceBox(): Promise<VialBoundingBox | null> {
  return measureVial(REFERENCE_URL);
}

/**
 * Computes uniform scale and translation so the vial bounding box matches
 * the reference vial height and centers at x=0.5, y=ref.cy.
 *
 * When scale * box.w or scale * box.h would exceed 0.98 of the container,
 * scale is clamped to prevent clipping the vial, and limited=true is flagged.
 */
export function computeVialTransform(
  box: VialBoundingBox,
  ref: VialBoundingBox,
  urlForLogging?: string
): VialTransform {
  // Target height matches reference height
  let s = ref.h / box.h;
  let limited = false;

  // Maximum allowed dimension fraction inside container
  const maxFraction = 0.98;

  // Check if scaled vial would overflow container boundaries
  if (box.h * s > maxFraction) {
    s = maxFraction / box.h;
    limited = true;
  }
  if (box.w * s > maxFraction) {
    s = maxFraction / box.w;
    limited = true;
  }

  // Transform origin at center of the vial bounding box
  const originX = box.cx * 100;
  const originY = box.cy * 100;

  // Translation to land bbox center at x=0.5 (50%) and y=ref.cy
  const tx = (0.5 - box.cx) * 100;
  const ty = (ref.cy - box.cy) * 100;

  // Log once per URL for verification on live site
  if (urlForLogging && !loggedUrls.has(urlForLogging)) {
    loggedUrls.add(urlForLogging);
    const filename = getFilenameFromUrl(urlForLogging);
    const scaledW = box.w * s;
    const widthRatio = ref.w > 0 ? scaledW / ref.w : 1;

    console.info(
      `[vial] ${filename} h=${box.h.toFixed(3)} w=${box.w.toFixed(3)} cx=${box.cx.toFixed(3)} cy=${box.cy.toFixed(3)} scale=${s.toFixed(3)} limited=${limited}`
    );

    // Warn if widths differ by > 3% (aspect ratio / vial shape difference)
    if (Math.abs(widthRatio - 1) > 0.03) {
      console.warn(
        `[vial] ${filename} scaled width differs from reference by ${((widthRatio - 1) * 100).toFixed(1)}% (scaledW=${scaledW.toFixed(3)} refW=${ref.w.toFixed(3)}). Preserving un-distorted uniform scale.`
      );
    }
  }

  return {
    scale: s,
    tx,
    ty,
    originX,
    originY,
    limited,
  };
}
